import {FC, useContext, useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {Badge, Button, Form, Modal} from 'react-bootstrap'
import {addDays, format, parseISO} from 'date-fns'
import {useAuth, hasPermission, LABOR_SCHEDULING_PERMS} from '../../../auth'
import LaborScheduleActualContext from '../../context/LaborScheduleActualContext'
import {getProposal} from '../../core/_requests'
import {
  LaborProposalApplyMode,
  LaborProposalModel,
  LaborProposalShiftModel,
} from '../../core/_models'
import {getPositionDisplayName} from '../../helpers/generals'
import {ProposalAspects} from './ProposalAspects'
import {conflictLabel, ruleLines, shiftBadgeClass, unavailableLines} from './availabilityNow'
import {useProposalActionRunner, useRecommendedSchedule} from './useRecommendedSchedule'

const modalsRoot = document.getElementById('root-modals') || document.body

const shiftsByDay = (shifts: LaborProposalShiftModel[], weekStart: string) => {
  const start = parseISO(weekStart)
  return Array.from({length: 7}, (_, i) => {
    const day = addDays(start, i)
    const key = format(day, 'yyyy-MM-dd')
    return {day, shifts: shifts.filter((s) => s.date_key === key)}
  })
}

// work_hours is already net of the shift's unpaid break, which is what makes
// this comparable to the current schedule's hours.
const hoursOf = (shifts: LaborProposalShiftModel[]) =>
  shifts.reduce((total, s) => total + (s.work_hours || 0), 0)

const anyBreaks = (shifts: LaborProposalShiftModel[]) =>
  shifts.some((s) => s.break_start_time && s.break_end_time)

/**
 * The break on a shift, for the badge's tooltip.
 *
 * A tooltip rather than badge text: every shift of 6h+ has one, so printing it
 * inline would double the length of every badge to say the same thing.
 */
const breakLabel = (shift: LaborProposalShiftModel): string | undefined => {
  if (!shift.break_start_time || !shift.break_end_time) {
    return undefined
  }
  const start = parseISO(shift.break_start_time)
  const end = parseISO(shift.break_end_time)
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000)
  return `Break ${format(start, 'HH:mm')}–${format(end, 'HH:mm')} (${minutes}m unpaid)`
}

/**
 * The recommended-schedule prompt.
 *
 * Opens by itself when the backend has a proposal worth showing for the
 * selected store, and is reachable afterwards from the Options menu. Opening it
 * records the read receipt, so it never auto-opens twice however it is closed;
 * from there the manager can apply it (replacing the week or adding to it) or
 * dismiss it, which sticks across the nightly regenerations.
 *
 * Dismissing silences the prompt, it does not remove the recommendation: the
 * decision is the store's (one row per location-week), and the recommendation
 * keeps its place in the Options menu so anyone can reopen it — or change their
 * mind and apply it.
 */
const RecommendedScheduleModal: FC = () => {
  const {currentUser} = useAuth()
  const {laborScheduleActual, setLaborScheduleActual} = useContext(LaborScheduleActualContext)
  const {pendingProposal, proposalModalOpen, closeModal, markSeen, rejectProposal, acceptProposal} =
    useRecommendedSchedule()
  const runAction = useProposalActionRunner()

  const [detail, setDetail] = useState<LaborProposalModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<LaborProposalApplyMode>('replace')
  const [reason, setReason] = useState('')
  const [confirmingDismiss, setConfirmingDismiss] = useState(false)

  const canApply = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.create)
  const canDismiss = hasPermission(currentUser, LABOR_SCHEDULING_PERMS.edit)

  useEffect(() => {
    if (!proposalModalOpen || !pendingProposal) return
    setMode('replace')
    setReason('')
    setConfirmingDismiss(false)
    setLoading(true)
    let cancelled = false
    getProposal(pendingProposal.uuid).then((full) => {
      if (cancelled) return
      setDetail(full ?? null)
      setLoading(false)
    })
    // Being on screen IS the read receipt — recorded here rather than on a
    // button so dismissing the modal with the X or the backdrop counts too.
    markSeen()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalModalOpen, pendingProposal])

  const days = useMemo(
    () => (detail ? shiftsByDay(detail.shifts, detail.week_start) : []),
    [detail]
  )

  if (!proposalModalOpen || !pendingProposal) return null

  const weekLabel = format(parseISO(pendingProposal.week_start), 'MMM d, yyyy')
  const currentShiftCount = detail?.current.shift_count ?? 0
  // Two states now wear the same 'dismissed' decision, and this banner is only
  // true of one of them. A rejected recommendation stays reopenable from the
  // Options menu, so 'dismissed' no longer implies "you rejected an EARLIER run
  // and this is a better one" — that is only the case when the decision names a
  // different proposal than the one on screen.
  const decision = pendingProposal.decision
  const wasDismissed =
    decision?.decision === 'dismissed' && decision.proposal_uuid !== pendingProposal.uuid

  const handleApply = () =>
    runAction(
      async () => {
        const schedule = await acceptProposal(mode)
        if (schedule) setLaborScheduleActual(schedule)
      },
      {
        title: 'Recommended Schedule Applied',
        message:
          mode === 'replace'
            ? `Week of ${weekLabel} replaced with the recommended schedule.`
            : `Recommended shifts added to the week of ${weekLabel}.`,
      },
      'Unable to apply the recommended schedule!'
    )

  const handleDismiss = () =>
    runAction(
      () => rejectProposal(reason.trim() || undefined),
      {
        title: 'Recommendation Dismissed',
        message: `The recommendation for the week of ${weekLabel} will not be shown again.`,
      },
      'Unable to dismiss the recommendation!'
    )

  return createPortal(
    <Modal
      id='fds_modal_recommended_schedule'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-800px'
      show={proposalModalOpen}
      onHide={closeModal}
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Recommended Schedule
          <span className='text-gray-600 fs-6 fw-semibold ms-2'>week of {weekLabel}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {wasDismissed && (
          <div className='alert alert-info py-3 px-4 fs-7'>
            You dismissed an earlier recommendation for this week — this one scores meaningfully
            better.
          </div>
        )}

        <div className='d-flex flex-wrap align-items-center gap-2 mb-5'>
          {pendingProposal.improvement_pct !== null ? (
            // The overall objective value, which is a weighted blend — worth
            // showing, but it cannot say better at WHAT, so it is labelled as
            // the summary it is and the per-aspect cards below carry the detail.
            <Badge bg='success' className='fs-7'>
              {pendingProposal.improvement_pct}% better overall
            </Badge>
          ) : (
            <Badge bg='info' className='fs-7'>
              This week has no schedule yet
            </Badge>
          )}
          {pendingProposal.unassigned_shift_count > 0 && (
            <Badge bg='warning' className='fs-7'>
              {pendingProposal.unassigned_shift_count} shift
              {pendingProposal.unassigned_shift_count === 1 ? '' : 's'} left unassigned
            </Badge>
          )}
          {pendingProposal.generated_at && (
            <span className='text-gray-600 fs-8'>
              generated {format(parseISO(pendingProposal.generated_at), 'MMM d, HH:mm')}
            </span>
          )}
        </div>

        <div className='row g-3 mb-6'>
          <div className='col-6'>
            <div className='border border-gray-300 border-dashed rounded p-4'>
              <div className='text-gray-600 fs-8 text-uppercase fw-bold'>Current schedule</div>
              <div className='fs-4 fw-bolder'>{currentShiftCount} shifts</div>
              <div className='text-gray-600 fs-7'>{detail?.current.total_hours ?? 0} hrs</div>
            </div>
          </div>
          <div className='col-6'>
            <div className='border border-primary border-dashed rounded p-4'>
              <div className='text-primary fs-8 text-uppercase fw-bold'>Recommended</div>
              <div className='fs-4 fw-bolder'>{pendingProposal.shift_count} shifts</div>
              <div className='text-gray-600 fs-7'>{pendingProposal.total_hours} hrs</div>
            </div>
          </div>
        </div>

        {/* Both tiles count hours actually worked. Said out loud because the
            recommendation gives long shifts a break the current schedule may
            not have, and "why is this fewer hours for the same coverage?" is
            otherwise a fair question. */}
        {anyBreaks(detail?.shifts || []) && (
          <div className='text-gray-500 fs-8 mb-6'>
            Hours exclude unpaid breaks — recommended shifts get 30 minutes from 6 hours, 60 from 8.
          </div>
        )}

        {/* Prefers the detail's copy: same numbers, but it is the fresher read
            and it is what /pending falls back to before the fetch lands. */}
        <ProposalAspects aspects={detail?.improvements ?? pendingProposal.improvements} />

        {/* The cards above were scored when the week was generated; this is
            availability as it is now, so a later "unavailable" still shows. */}
        {[...unavailableLines(detail), ...ruleLines(detail)].length > 0 && (
          <div className='alert alert-danger py-3 fs-7 mb-5' role='alert'>
            {[...unavailableLines(detail), ...ruleLines(detail)].map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        )}

        <label className='form-label fw-bold'>Shifts by day:</label>
        {loading ? (
          <div className='text-gray-600 fs-7'>Loading…</div>
        ) : (
          <div className='mh-300px overflow-auto'>
            <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-2'>
              <thead>
                <tr className='fs-8 text-gray-500'>
                  <th className='w-125px'>Day</th>
                  <th className='w-75px'>Hours</th>
                  <th>Shifts</th>
                </tr>
              </thead>
              <tbody>
                {days.map(({day, shifts}) => (
                  <tr key={day.toISOString()}>
                    <td className='fw-bold'>{format(day, 'EEE MMM d')}</td>
                    <td className='text-gray-700'>{Math.round(hoursOf(shifts) * 10) / 10}</td>
                    <td>
                      {shifts.length === 0 ? (
                        <span className='text-gray-500 fs-8'>—</span>
                      ) : (
                        <div className='d-flex flex-wrap gap-1'>
                          {shifts.map((shift, i) => (
                            <span
                              key={`${shift.date_key}-${i}`}
                              title={
                                [conflictLabel(shift), breakLabel(shift)]
                                  .filter(Boolean)
                                  .join(' · ') || undefined
                              }
                              className={`badge fs-9 ${shiftBadgeClass(shift)}`}
                            >
                              {shift.availability_conflict === 'unavailable' && '⚠ '}
                              {format(parseISO(shift.start_time), 'HH:mm')}–
                              {format(parseISO(shift.end_time), 'HH:mm')}
                              {' · '}
                              {shift.employee_name || 'Unassigned'}
                              {shift.shift_role
                                ? ` (${getPositionDisplayName(shift.shift_role)})`
                                : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canApply && (
          <div className='mt-6'>
            <label className='form-label fw-bold'>When applying:</label>
            <Form.Check
              type='radio'
              id='proposal-mode-replace'
              name='proposal-mode'
              className='fs-7'
              checked={mode === 'replace'}
              onChange={() => setMode('replace')}
              label={
                currentShiftCount > 0
                  ? `Replace the week — the ${currentShiftCount} shift${
                      currentShiftCount === 1 ? '' : 's'
                    } already scheduled are deleted first`
                  : 'Replace the week (nothing scheduled yet)'
              }
            />
            <Form.Check
              type='radio'
              id='proposal-mode-append'
              name='proposal-mode'
              className='fs-7'
              checked={mode === 'append'}
              onChange={() => setMode('append')}
              label='Add to the existing shifts — nothing is deleted'
            />
            {laborScheduleActual?.can_edit_shifts === false && (
              <div className='text-danger fs-8 mt-2 fw-semibold'>
                This week's schedule is locked against editing — applying will be rejected.
              </div>
            )}
          </div>
        )}

        {confirmingDismiss && (
          <div className='mt-6'>
            <label className='form-label fw-bold'>Why are you dismissing it? (optional)</label>
            <textarea
              className='form-control form-control-sm'
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className='text-gray-600 fs-8 mt-1'>
              This week won't be recommended again unless a later run scores meaningfully better.
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {canDismiss &&
          (confirmingDismiss ? (
            <Button variant='light-danger' onClick={handleDismiss}>
              Confirm dismiss
            </Button>
          ) : (
            <Button variant='light-danger' onClick={() => setConfirmingDismiss(true)}>
              Dismiss
            </Button>
          ))}
        <Button variant='secondary' onClick={closeModal}>
          Later
        </Button>
        {canApply && (
          <Button variant='primary' disabled={loading || !detail} onClick={handleApply}>
            {mode === 'replace' ? 'Replace week with this' : 'Add these shifts'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>,
    modalsRoot
  )
}

export {RecommendedScheduleModal}
